/**
 * Client-side export + copy helpers for FS-557 call-transcript artifacts.
 *
 * The transcript body is born-markdown, so `.txt` and `.md` are exact
 * client-side serializations. The "Word" option produces a Word-openable HTML
 * document (dependency-free); a true OOXML `.docx` would need a zip lib, so we
 * ship the HTML-as-doc form Word and Google Docs both open cleanly.
 */

export type TranscriptExportFormat = 'txt' | 'md' | 'doc'

/** Strip a leading markdown H1 so a plain-text export reads cleanly. */
function markdownToPlainText(markdown: string): string {
  return markdown
    .replace(/^#{1,6}\s+/gm, '') // headings → bare text
    .replace(/\*\*(.*?)\*\*/g, '$1') // bold
    .replace(/\*(.*?)\*/g, '$1') // italic
    .replace(/`([^`]+)`/g, '$1') // inline code
    .replace(/^\s*[-*]\s+/gm, '• ') // bullets
    .trimEnd()
}

/** Minimal markdown → HTML for the Word export (headings, bold, line breaks). */
function markdownToHtml(markdown: string): string {
  const escape = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  const body = markdown
    .split('\n')
    .map((line) => {
      const h = /^(#{1,6})\s+(.*)$/.exec(line)
      if (h) {
        const level = h[1].length
        // Bold inside a heading must still render (e.g. `## **Title**`).
        const text = escape(h[2]).replace(/\*\*(.*?)\*\*/g, '<b>$1</b>')
        return `<h${level}>${text}</h${level}>`
      }
      if (line.trim() === '') return '<br/>'
      const inline = escape(line).replace(/\*\*(.*?)\*\*/g, '<b>$1</b>')
      return `<p>${inline}</p>`
    })
    .join('\n')
  return `<!DOCTYPE html><html><head><meta charset="utf-8"/></head><body>${body}</body></html>`
}

const FORMAT_META: Record<
  TranscriptExportFormat,
  { ext: string; mime: string; render: (md: string) => string }
> = {
  txt: { ext: 'txt', mime: 'text/plain;charset=utf-8', render: markdownToPlainText },
  md: { ext: 'md', mime: 'text/markdown;charset=utf-8', render: (md) => md },
  doc: { ext: 'doc', mime: 'application/msword', render: markdownToHtml },
}

/** Slugify a document title into a safe filename stem. */
export function transcriptFileStem(title: string): string {
  const stem = title
    .trim()
    .replace(/\.[a-z0-9]+$/i, '') // drop any existing extension
    // Unicode-aware: keep letters/digits (any script) + spaces/hyphens, so an
    // accented or CJK/Arabic title isn't stripped to the 'transcript' fallback.
    .replace(/[^\p{L}\p{N}\s-]/gu, '')
    .replace(/\s+/g, '_')
    .slice(0, 80)
  return stem || 'transcript'
}

/** Trigger a browser download of the transcript markdown in the chosen format. */
export function downloadTranscript(
  markdown: string,
  title: string,
  format: TranscriptExportFormat,
): void {
  const meta = FORMAT_META[format]
  const blob = new Blob([meta.render(markdown)], { type: meta.mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${transcriptFileStem(title)}.${meta.ext}`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** Copy the raw transcript markdown to the clipboard. Returns success. */
export async function copyTranscript(markdown: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(markdown)
    return true
  } catch {
    return false
  }
}
